import { DeleteOutlined, FilterOutlined } from '@ant-design/icons';
import { App, AutoComplete, Button, Card, Col, InputNumber, Row, Select, Tag, Tooltip } from 'antd';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import {
  removePmContractProjectWorkPlaceItem,
  updatePmContractProjectWorkPlaceItem,
} from '../../../../../contextRedux/PMContractProject/PmContractModelProject';
import { CustomTable } from '../../../../../customComponents/table/CustomTable';
import { RootState } from '../../../../../store';
import './SummaryTab.css';
import SummaryTotalRow from './SummaryTotalRow/SummaryTotalRow';

const { Option } = AutoComplete;

const SummaryTab: React.FC<any> = ({}) => {
  const isMobile = useMediaQuery({ maxWidth: 1003 });
  const { message } = App.useApp();
  const dispatch = useDispatch();
  const [, setSearchTerm] = useState<string>('');

  // Filter states
  const [filters, setFilters] = useState<any>({
    workPlace: [],
    stage: [],
    chiefTehnician: [],
    tehnician: [],
    systemType: [],
    contactPerson: [],
  });

  // Filtered data state
  const [filteredData, setFilteredData] = useState<any[]>([]);

  const pmContractProject = useSelector((state: RootState) => state.models.pmContractProject);
  const company = useSelector((state: RootState) => state.models.pmContractProject.company);
  const workPlaces = useSelector((state: RootState) => state.models.pmContractProject.company?.workPlaces);
  const addedDevices = useSelector(
    (state: RootState) => state?.models?.pmContractProject.pmContractWorkPlaceItems
  )?.map((ad: any) => {
    const { piece, addition, unitmeasure, laborPriceOnHour, metadata } = ad;
    const devicePrice = Number(metadata?.deviceType?.price ?? 0);

    return {
      ...ad,
      price: Number(
        unitmeasure == 'hour'
          ? piece * laborPriceOnHour
          : (piece * devicePrice + (piece * devicePrice * addition) / 100).toFixed(2)
      ),
    };
  });

  // Extract unique filter options
  const filterOptions = useMemo(() => {
    const options = {
      workPlace: new Set<string>(),
      stage: new Set<string>(),
      chiefTehnician: new Set<string>(),
      tehnician: new Set<string>(),
      systemType: new Set<string>(),
      contactPerson: new Set<string>(),
    };

    addedDevices?.forEach((device: any) => {
      if (device?.workPlace?.name) options.workPlace.add(device.workPlace.name);
      if (device?.pmContractWorkPlaceItemStage?.name) options.stage.add(device.pmContractWorkPlaceItemStage.name);
      if (device?.chiefTehnician?.name) options.chiefTehnician.add(device.chiefTehnician.name);
      if (device?.systemType?.name) options.systemType.add(device.systemType.name);
      if (device?.agent?.name) options.contactPerson.add(device.agent.name);

      // Extract all technicians
      device?.pmContractWorkPlaceItemTehnicians?.forEach((tech: any) => {
        if (tech?.user?.name) options.tehnician.add(tech.user.name);
      });
    });

    return {
      workPlace: Array.from(options.workPlace),
      stage: Array.from(options.stage),
      chiefTehnician: Array.from(options.chiefTehnician),
      tehnician: Array.from(options.tehnician),
      systemType: Array.from(options.systemType),
      contactPerson: Array.from(options.contactPerson),
    };
  }, [addedDevices]);

  // Apply filters to data
  useEffect(() => {
    // Explicitly type result as an array of any
    let result: any[] = [...addedDevices];

    // Apply workPlace filter
    if (filters.workPlace.length > 0) {
      result = result.filter((device: any) => filters.workPlace.includes(device?.workPlace?.name));
    }

    // Apply stage filter
    if (filters.stage.length > 0) {
      result = result.filter((device: any) => filters.stage.includes(device?.pmContractWorkPlaceItemStage?.name));
    }

    // Apply chiefTehnician filter
    if (filters.chiefTehnician.length > 0) {
      result = result.filter((device: any) => filters.chiefTehnician.includes(device?.chiefTehnician?.name));
    }

    // Apply systemType filter
    if (filters.systemType.length > 0) {
      result = result.filter((device: any) => filters.systemType.includes(device?.systemType?.name));
    }

    // Apply contactPerson filter
    if (filters.contactPerson.length > 0) {
      result = result.filter((device: any) => filters.contactPerson.includes(device?.agent?.name));
    }

    // Apply technician filter (more complex as it's an array)
    if (filters.tehnician.length > 0) {
      result = result.filter((device: any) => {
        const deviceTechnicians = device?.pmContractWorkPlaceItemTehnicians?.map((tech: any) => tech?.user?.name) || [];
        return filters.tehnician.some((techName: string) => deviceTechnicians.includes(techName));
      });
    }

    setFilteredData(result);
  }, [addedDevices, filters]);

  const calculatePriceWithAddition = (record: any) => {
    const { piece, addition, metadata } = record;
    const devicePrice = Number.parseFloat((metadata?.deviceType?.price ?? 0).toFixed(2));

    return Number.parseFloat((piece * devicePrice + (piece * devicePrice * addition) / 100).toFixed(2));
  };

  const handleRemoveEntry = useCallback(
    (_: any, index: number) => {
      dispatch(removePmContractProjectWorkPlaceItem(index));
    },
    [addedDevices]
  );

  const getTotalPriceWithAdition = () => {
    return Number.parseFloat(
      filteredData
        .reduce((total: number, item: any) => {
          const totalPrice = item.price;
          return total + totalPrice;
        }, 0)
        .toFixed(2)
    );
  };

  const getTotalAchizitionPrice = () => {
    return Number.parseFloat(
      filteredData
        .reduce((total: number, item: any) => {
          const totalPrice = item?.device?.price * item.piece;
          return total + totalPrice;
        }, 0)
        .toFixed(2)
    );
  };

  const handleQuantityChange = useCallback(
    (record: any, value: number, _key?: string) => {
      if (!value) return;

      const updatedRecord = { ...record, piece: value, lastPiece: record.piece };
      const factor = updatedRecord.piece / updatedRecord.lastPiece;

      updatedRecord.price = Number((updatedRecord.price * factor).toFixed(2));
      updatedRecord.laborPrice = Number((updatedRecord.laborPrice * factor).toFixed(2));
      updatedRecord.laborTime = Number((updatedRecord.laborTime * factor).toFixed(2));

      if (updatedRecord.laborPrice < updatedRecord.device.laborPricePM) {
        updatedRecord.laborPrice = updatedRecord.device.laborPricePM;
      }

      if (updatedRecord.laborTime < updatedRecord.device.laborTimePM) {
        updatedRecord.laborTime = updatedRecord.device.laborTimePM;
      }

      dispatch(
        updatePmContractProjectWorkPlaceItem({
          index: addedDevices.findIndex(
            (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
          ),
          updates: updatedRecord,
        })
      );
    },
    [addedDevices, dispatch]
  );

  const handleAdditionChange = useCallback(
    (record: any, value: number, _key: any, isFromLaborTable: boolean = false) => {
      if (!value) {
        return;
      }
      const updatedRecord = {
        ...record,
        addition: Number.parseFloat(value.toFixed(2)),
        price: isFromLaborTable
          ? Number.parseFloat(record.price.toFixed(2))
          : calculatePriceWithAddition({ ...record, addition: Number.parseFloat(value.toFixed(2)) }),
      };
      dispatch(
        updatePmContractProjectWorkPlaceItem({
          index: addedDevices.findIndex(
            (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
          ),
          updates: updatedRecord,
        })
      );
    },
    [addedDevices, dispatch]
  );

  const handlePriceChange = useCallback(
    (record: any, value: number, _key: string) => {
      if (!value) {
        return;
      }
      const deviceTypePrice = Number.parseFloat(record.metadata.deviceType.price.toFixed(2));
      const piece = record.piece;
      const addition = Number.parseFloat(
        (((value - deviceTypePrice * piece) / (deviceTypePrice * piece)) * 100).toFixed(2)
      );

      const updatedRecord = {
        ...record,
        price: Number.parseFloat(value.toFixed(2)),
        addition: Number.parseFloat((addition < 1 ? 1 : addition).toFixed(2)),
      };

      const debouncedDispatch = debounce(
        () =>
          dispatch(
            updatePmContractProjectWorkPlaceItem({
              index: addedDevices.findIndex(
                (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
              ),
              updates: updatedRecord,
            })
          ),
        1200
      );

      debouncedDispatch();
    },
    [addedDevices, dispatch]
  );

  const handleSelectAgent = (record: any, value: string) => {
    // Find the selected agent by its name
    const selectedAgent =
      company.contactPersons?.find((agent: any) => agent.name === value) ??
      workPlaces
        ?.filter((w: any) => w.id == record.workPlaceId)
        ?.flatMap((workPlace: any) => workPlace.contactPersons || [])
        ?.find((agent: any) => agent.name === value);

    if (!selectedAgent) {
      message.error('Agent nu a fost gasit!');
      return;
    }

    // Find the index of the record to update in addedDevices
    const recordIndex = addedDevices.findIndex(
      (device: any) => device.workPlaceId === record.workPlaceId && device.deviceTypeId === record.deviceTypeId
    );

    if (recordIndex === -1) {
      message.error('Inregistrarea nu a fost gasita!');
      return;
    }

    // Dispatch the update for the specific record
    dispatch(
      updatePmContractProjectWorkPlaceItem({
        index: recordIndex,
        updates: {
          agent: selectedAgent,
          agentId: selectedAgent.id,
        },
      })
    );

    message.success(`Agentul a fost modificat cu ${selectedAgent.name}.`);
  };

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleFilter = (inputValue: string, option: any) => {
    return option.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
  };

  // Handle filter changes
  const handleFilterChange = (filterType: string, values: string[]) => {
    setFilters((prev: any) => ({
      ...prev,
      [filterType]: values,
    }));
  };

  // New function to calculate totals by category
  const calculateTotalsByCategory = useCallback(() => {
    const workPlaceTotals: Record<string, { acquisition: number; withAddition: number }> = {};
    const stageTotals: Record<string, { acquisition: number; withAddition: number }> = {};

    filteredData.forEach((item: any) => {
      const workPlaceName = item?.workPlace?.name || 'Unknown';
      const stageName = item?.pmContractWorkPlaceItemStage?.name || 'Unknown';
      const acquisitionPrice = Number((item?.device?.price || 0) * item.piece);
      const priceWithAddition = Number(item.price || 0);

      // Add to workplace totals
      if (!workPlaceTotals[workPlaceName]) {
        workPlaceTotals[workPlaceName] = { acquisition: 0, withAddition: 0 };
      }
      workPlaceTotals[workPlaceName].acquisition += acquisitionPrice;
      workPlaceTotals[workPlaceName].withAddition += priceWithAddition;

      // Add to stage totals
      if (!stageTotals[stageName]) {
        stageTotals[stageName] = { acquisition: 0, withAddition: 0 };
      }
      stageTotals[stageName].acquisition += acquisitionPrice;
      stageTotals[stageName].withAddition += priceWithAddition;
    });

    return { workPlaceTotals, stageTotals };
  }, [filteredData]);

  // Create filter section component
  const renderFilterSection = () => {
    const hasActiveFilters = Object.values(filters).some((filter: any) => filter.length > 0);

    return (
      <div className='summary-filter-section'>
        <div className='filter-header'>
          <h3>Filtrează Dispozitivele</h3>
          {hasActiveFilters && (
            <Button
              type='primary'
              onClick={() =>
                setFilters({
                  workPlace: [],
                  stage: [],
                  chiefTehnician: [],
                  tehnician: [],
                  systemType: [],
                  contactPerson: [],
                })
              }
            >
              Reset All Filters
            </Button>
          )}
        </div>

        <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
          <Col xs={24} sm={12} md={8}>
            <span className='filter-label'>Locație</span>
            <Select
              mode='multiple'
              style={{ width: '100%' }}
              placeholder='Filtrează după locație'
              value={filters.workPlace}
              onChange={(values) => handleFilterChange('workPlace', values)}
              options={filterOptions.workPlace.map((option) => ({ value: option, label: option }))}
              maxTagCount={2}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={8}>
            <span className='filter-label'>Etapă</span>
            <Select
              mode='multiple'
              style={{ width: '100%' }}
              placeholder='Filtrează după etapă'
              value={filters.stage}
              onChange={(values) => handleFilterChange('stage', values)}
              options={filterOptions.stage.map((option) => ({ value: option, label: option }))}
              maxTagCount={2}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={8}>
            <span className='filter-label'>Tehnician Șef</span>
            <Select
              mode='multiple'
              style={{ width: '100%' }}
              placeholder='Filtrează după tehnician șef'
              value={filters.chiefTehnician}
              onChange={(values) => handleFilterChange('chiefTehnician', values)}
              options={filterOptions.chiefTehnician.map((option) => ({ value: option, label: option }))}
              maxTagCount={2}
              allowClear
            />
          </Col>
        </Row>
        <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
          <Col xs={24} sm={12} md={8}>
            <span className='filter-label'>Tehnician</span>
            <Select
              mode='multiple'
              style={{ width: '100%' }}
              placeholder='Filtrează după tehnician'
              value={filters.tehnician}
              onChange={(values) => handleFilterChange('tehnician', values)}
              options={filterOptions.tehnician.map((option) => ({ value: option, label: option }))}
              maxTagCount={2}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={8}>
            <span className='filter-label'>Sistem</span>
            <Select
              mode='multiple'
              style={{ width: '100%' }}
              placeholder='Filtrează după sistem'
              value={filters.systemType}
              onChange={(values) => handleFilterChange('systemType', values)}
              options={filterOptions.systemType.map((option) => ({ value: option, label: option }))}
              maxTagCount={2}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={8}>
            <span className='filter-label'>Persoană de Contact</span>
            <Select
              mode='multiple'
              style={{ width: '100%' }}
              placeholder='Filtrează după persoană de contact'
              value={filters.contactPerson}
              onChange={(values) => handleFilterChange('contactPerson', values)}
              options={filterOptions.contactPerson.map((option) => ({ value: option, label: option }))}
              maxTagCount={2}
              allowClear
            />
          </Col>
        </Row>

        {hasActiveFilters && (
          <div className='active-filters'>
            {Object.entries(filters).map(
              ([filterType, values]) =>
                (values as string[]).length > 0 && (
                  <div key={filterType} className='filter-category'>
                    <span className='filter-type'>{getFilterLabel(filterType)}:</span>
                    <div className='filter-tags'>
                      {(values as string[]).map((value) => (
                        <Tag
                          key={`${filterType}-${value}`}
                          closable
                          onClose={() => {
                            const newValues = (values as string[]).filter((v) => v !== value);
                            handleFilterChange(filterType, newValues);
                          }}
                          color={getTagColor(filterType)}
                        >
                          {value}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )
            )}
          </div>
        )}
      </div>
    );
  };

  // Helper function to get filter label
  const getFilterLabel = (filterType: string) => {
    const labels: Record<string, string> = {
      workPlace: 'Locație',
      stage: 'Etapă',
      chiefTehnician: 'Tehnician Șef',
      tehnician: 'Tehnician',
      systemType: 'Sistem',
      contactPerson: 'Persoană de Contact',
    };
    return labels[filterType] || filterType;
  };

  // Helper function to get tag color
  const getTagColor = (filterType: string) => {
    const colors: Record<string, string> = {
      workPlace: 'blue',
      stage: 'green',
      chiefTehnician: 'purple',
      tehnician: 'magenta',
      systemType: 'cyan',
      contactPerson: 'orange',
    };
    return colors[filterType] || 'default';
  };

  // Create filter dropdown menu for a column
  const createFilterDropdown = (filterType: string, options: string[]) => {
    return {
      filterDropdown: ({ confirm }: any) => (
        <div style={{ padding: 8, minWidth: 200 }}>
          <Select
            mode='multiple'
            style={{ width: '100%' }}
            placeholder={`Filter by ${filterType}`}
            value={filters[filterType as keyof typeof filters] as string[]}
            onChange={(values) => {
              handleFilterChange(filterType, values);
              confirm();
            }}
            options={options.map((option) => ({ value: option, label: option }))}
          />
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              size='small'
              onClick={() => {
                handleFilterChange(filterType, []);
                confirm();
              }}
            >
              Reset
            </Button>
            <Button type='primary' size='small' onClick={() => confirm()}>
              Apply
            </Button>
          </div>
        </div>
      ),
      filterIcon: (filtered: boolean) => <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      filtered: filters[filterType as keyof typeof filters].length > 0,
    };
  };

  // Render detailed price summary
  const renderDetailedPriceSummary = () => {
    //    const { workPlaceTotals, stageTotals } = calculateTotalsByCategory();
    const { workPlaceTotals } = calculateTotalsByCategory();

    return (
      <div className='detailed-price-summary'>
        <div className='summary-section'>
          <h3 className='summary-title'>Sumar Prețuri pe Locație</h3>
          <div className='summary-cards'>
            {Object.entries(workPlaceTotals).map(([workPlace, totals]) => (
              <Card key={`wp-${workPlace}`} className='summary-card workplace-card'>
                <div className='card-header'>
                  <span className='location-badge'>{workPlace}</span>
                </div>
                <div className='card-content'>
                  <div className='price-row'>
                    <span className='price-label'>Preț de Achiziție:</span>
                    <span className='price-value acquisition'>{totals.acquisition.toFixed(2)} RON</span>
                  </div>
                  <div className='price-row'>
                    <span className='price-label'>Preț cu Adaos:</span>
                    <span className='price-value with-addition'>{totals.withAddition.toFixed(2)} RON</span>
                  </div>
                  <div className='price-row profit'>
                    <span className='price-label'>Profit:</span>
                    <span className='price-value profit'>
                      {(totals.withAddition - totals.acquisition).toFixed(2)} RON (
                      {((totals.withAddition / totals.acquisition - 1) * 100).toFixed(2)}%)
                    </span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* <div className='summary-section'>
          <h3 className='summary-title'>Sumar Prețuri pe Etapă</h3>
          <div className='summary-cards'>
            {Object.entries(stageTotals).map(([stage, totals]) => (
              <Card key={`stage-${stage}`} className='summary-card stage-card'>
                <div className='card-header'>
                  <span className='stage-badge'>{stage}</span>
                </div>
                <div className='card-content'>
                  <div className='price-row'>
                    <span className='price-label'>Preț de Achiziție:</span>
                    <span className='price-value acquisition'>{totals.acquisition.toFixed(2)} RON</span>
                  </div>
                  <div className='price-row'>
                    <span className='price-label'>Preț cu Adaos:</span>
                    <span className='price-value with-addition'>{totals.withAddition.toFixed(2)} RON</span>
                  </div>
                  <div className='price-row profit'>
                    <span className='price-label'>Profit:</span>
                    <span className='price-value profit'>
                      {(totals.withAddition - totals.acquisition).toFixed(2)} RON (
                      {((totals.withAddition / totals.acquisition - 1) * 100).toFixed(2)}%)
                    </span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div> */}

        <div className='total-summary'>
          <Card className='total-card'>
            <div className='card-header'>
              <h3>Sumar Total</h3>
            </div>
            <div className='card-content'>
              <div className='price-row'>
                <span className='price-label'>Total Preț de Achiziție:</span>
                <span className='price-value acquisition'>{getTotalAchizitionPrice()} RON</span>
              </div>
              <div className='price-row'>
                <span className='price-label'>Total Preț cu Adaos:</span>
                <span className='price-value with-addition'>{getTotalPriceWithAdition()} RON</span>
              </div>
              <div className='price-row profit'>
                <span className='price-label'>Profit Total:</span>
                <span className='price-value profit'>
                  {(getTotalPriceWithAdition() - getTotalAchizitionPrice()).toFixed(2)} RON (
                  {((getTotalPriceWithAdition() / getTotalAchizitionPrice() - 1) * 100).toFixed(2)}%)
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  };

  // Update the columns definition to use the createFilterDropdown function correctly
  const columns = useMemo(() => {
    return [
      {
        title: 'No.',
        dataIndex: '',
        key: 'index',
        render: (_text: string, _record: any, index: number) => index + 1 + '.',
        width: 60,
      },
      {
        title: 'Locație',
        dataIndex: ['workPlace', 'name'],
        key: 'workPlace',
        ...createFilterDropdown('workPlace', filterOptions.workPlace),
        render: (text: string) => <span style={{ fontWeight: 'bold' }}>{text}</span>,
      },
      {
        title: 'Etapă',
        dataIndex: ['pmContractWorkPlaceItemStage', 'name'],
        key: 'stage',
        ...createFilterDropdown('stage', filterOptions.stage),
        render: (text: string) => (
          <span
            style={{
              backgroundColor: '#e6f7ff',
              padding: '2px 8px',
              borderRadius: '4px',
              border: '1px solid #91d5ff',
            }}
          >
            {text}
          </span>
        ),
      },
      {
        title: 'Device',
        key: 'device',
        render: (record: any) => (
          <div style={{ display: 'grid' }}>
            <Tooltip title={record.device?.name}>
              <div className='ellipsis-cell'>
                <span>{record.device?.name}</span>
              </div>
            </Tooltip>
            {record?.device?.imageLink &&
            record?.device?.imageLink[0] != '' &&
            record?.device?.imageLink[0].includes('.jpg', '.png') ? (
              <img src={record?.device?.imageLink[0]} alt='' className='img-large-view' />
            ) : (
              ''
            )}
          </div>
        ),
      },
      {
        title: 'Tehnician Sef',
        dataIndex: ['chiefTehnician', 'name'],
        key: 'chiefTehnicianName',
        ...createFilterDropdown('chiefTehnician', filterOptions.chiefTehnician),
        width: 150,
      },
      {
        title: 'Tehnicieni',
        dataIndex: 'tehnicieni',
        key: 'tehnicieni',
        ...createFilterDropdown('tehnician', filterOptions.tehnician),
        render: (_text: string, record: any, _index: number) => (
          <>
            {record?.pmContractWorkPlaceItemTehnicians?.length > 0 ? (
              record?.pmContractWorkPlaceItemTehnicians?.map((tehnician: any, index: any) => {
                return <div key={index}>{'[' + tehnician?.user?.name + ']'}</div>;
              })
            ) : (
              <div>----</div>
            )}
          </>
        ),
        width: 150,
      },
      {
        title: 'Persoana De Contact',
        dataIndex: ['agent', 'name'],
        key: 'agent',
        ...createFilterDropdown('contactPerson', filterOptions.contactPerson),
        render: (text: string, record: any) => (
          <Select
            placeholder='Select an agent'
            onSelect={(e) => handleSelectAgent(record, e)}
            onChange={handleSearchTermChange}
            filterOption={handleFilter}
            value={text}
            style={{ width: '100%' }}
          >
            {[
              // If agent exists in equipmentTab, add it as the first option
              pmContractProject?.equipmentTab?.agent && !pmContractProject?.equipmentTab?.isOfferSelected && (
                <Option key={pmContractProject.equipmentTab.agent.id} value={pmContractProject.equipmentTab.agent.name}>
                  {pmContractProject.equipmentTab.agent.name}
                </Option>
              ),
              // Combine all contactPersons from company and workPlaces
              ...Array.from(
                new Map(
                  [
                    // Company contactPersons if offer is selected
                    ...(company?.contactPersons?.length > 0 && pmContractProject?.equipmentTab?.isOfferSelected
                      ? company.contactPersons?.map((contactPerson: any) => ({
                          id: contactPerson.id,
                          name: contactPerson.name,
                          email: contactPerson.email,
                          isActive: contactPerson.isActive,
                          phone: contactPerson.phone,
                          role: contactPerson.role,
                        }))
                      : []),
                    // WorkPlaces' contactPersons
                    ...(workPlaces
                      ?.filter((w: any) => w?.id === record?.workPlaceId) // Match the current workPlaceId
                      ?.flatMap((workPlace: any) => workPlace?.contactPersons || []) // Flatten contactPersons
                      ?.map((contactPerson: any) => ({
                        id: contactPerson.id,
                        name: contactPerson.name,
                        email: contactPerson.email,
                        isActive: contactPerson.isActive,
                        phone: contactPerson.phone,
                        role: contactPerson.role,
                      })) || []),
                  ]?.map((contactPerson: any) => [contactPerson.name, contactPerson]) // Use 'name' as the key
                ).values() // Extract unique values by name
              )
                ?.filter(Boolean) // Remove undefined/null entries
                ?.map((contactPerson: any, index: number) => (
                  <Option key={index} value={contactPerson.name}>
                    {contactPerson.name}
                  </Option>
                )),
            ]}
          </Select>
        ),
      },
      {
        title: 'Sistem',
        dataIndex: ['systemType', 'name'],
        key: 'system',
        ...createFilterDropdown('systemType', filterOptions.systemType),
      },
      {
        title: 'Cantitate',
        dataIndex: 'piece',
        key: 'piece',
        editable: true,
        render: (text: string, record: any, key: any) => {
          return (
            <InputNumber
              value={text}
              min='1'
              disabled={!columns.find((column) => column.key === 'piece')?.editable}
              onChange={(value) => handleQuantityChange(record, parseFloat(value!), key)}
            />
          );
        },
      },
      {
        title: 'Adaos',
        dataIndex: 'addition',
        key: 'addition',
        editable: true,
        render: (text: string, record: any, key: any) => {
          return (
            <InputNumber
              min='1'
              value={text}
              disabled={!columns.find((column) => column.key === 'addition')?.editable}
              onChange={(value) => handleAdditionChange(record, parseFloat(value!), key)}
            />
          );
        },
      },
      {
        title: 'Pret de Achizitie',
        dataIndex: ['metadata', 'deviceType', 'price'],
        key: 'deviceTypePrice',
      },
      {
        title: 'Pret cu Adaos',
        dataIndex: 'price',
        key: 'price',
        editable: true,
        render: (text: any, record: any, key: any) => {
          return (
            <InputNumber
              min={1}
              value={text}
              disabled={!columns.find((column) => column.key === 'price')?.editable}
              onChange={(e) => {
                const inputValue = parseFloat(e ?? 1);
                handlePriceChange(record, inputValue, key);
              }}
            />
          );
        },
      },
      {
        title: 'Action',
        dataIndex: 'action',
        key: 'action',
        fixed: 'right',
        width: 100,
        render: (_: any, record: any, index: any) =>
          record?.deviceTypeId && (
            <Button
              type='link'
              onClick={() => handleRemoveEntry(record, index)}
              icon={<DeleteOutlined />}
              className='large-view-delete'
            >
              şterge
            </Button>
          ),
      },
    ];
  }, [
    filters,
    filterOptions,
    handleFilterChange,
    pmContractProject,
    company,
    workPlaces,
    handleSelectAgent,
    handleSearchTermChange,
    handleFilter,
    handleQuantityChange,
    handleAdditionChange,
    handlePriceChange,
    handleRemoveEntry,
  ]);

  const DeviceName = ({ name }: { name: string }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const maxLength = 70;
    return (
      <p className='deviceName'>
        <strong>{isExpanded || name.length <= maxLength ? name : `${name.slice(0, maxLength)}... `}</strong>
        {name.length > maxLength && (
          <a
            href='#'
            onClick={(e) => {
              e.preventDefault();
              setIsExpanded(!isExpanded);
            }}
            style={{ color: 'blue', cursor: 'pointer', marginLeft: '5px' }}
          >
            {isExpanded ? 'Read Less' : 'Read More'}
          </a>
        )}
      </p>
    );
  };

  //Mobile view render card
  const renderCard = (device: any, index: number) => (
    <Card
      key={device.id}
      title={
        <div>
          <div>
            {device.workPlace?.name} - {device.pmContractWorkPlaceItemStage?.name}
          </div>
          <div>Device {index + 1}</div>
        </div>
      }
      extra={
        <Button
          type='text'
          className='delete-btn'
          onClick={() => handleRemoveEntry(device, index)}
          icon={<DeleteOutlined />}
        />
      }
      className='device-card'
    >
      {device.device?.imageLink?.[0]?.match(/\.(jpg|png)$/i) && (
        <img src={device.device.imageLink[0]} alt='Device' className='cardImage' />
      )}

      <div className='device-name'>
        <DeviceName name={device.device?.name} />
      </div>

      <div className='info-grid'>
        <div className='info-item full-width'>
          <span className='info-label'>Persoana De Contact</span>
          <Select
            value={device.agent?.name}
            onChange={(value) => handleSelectAgent(device, value)}
            placeholder='Select contact'
          >
            {company?.contactPersons?.map((contactPerson: any, index: number) => (
              <Option key={index} value={contactPerson.name}>
                {contactPerson.name}
              </Option>
            ))}
          </Select>
        </div>

        <div className='info-item'>
          <span className='info-label'>Cantitate</span>
          <InputNumber value={device.piece} min={1} onChange={(value) => handleQuantityChange(device, value)} />
        </div>

        <div className='info-item'>
          <span className='info-label'>Adaos (%)</span>
          <InputNumber
            value={device.addition}
            min={1}
            onChange={(value) => handleAdditionChange(device, parseFloat(value!), null, false)}
          />
        </div>
      </div>

      <div className='price-section'>
        <div className='info-grid'>
          <div className='info-item'>
            <span className='info-label'>Pret de Achizitie</span>
            <span className='info-value'>{device.metadata?.deviceType?.price}</span>
          </div>

          <div className='info-item'>
            <span className='info-label'>Pret cu Adaos</span>
            <InputNumber
              value={device.price}
              min={1}
              onChange={(value) => {
                if (value !== null && !isNaN(value)) {
                  handlePriceChange(device, value, device.id);
                }
              }}
            />
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className='deviced-added-container'>
      <h3>Devices added:</h3>

      {/* Filter section */}
      {renderFilterSection()}

      {/* Unified table with all devices */}
      {isMobile ? (
        <div className='mobile-cards-container'>
          {filteredData.map(renderCard)}
          <SummaryTotalRow
            getTotalAchizitionPrice={getTotalAchizitionPrice}
            getTotalPriceWithAdition={getTotalPriceWithAdition}
            isMobile={true}
          />
        </div>
      ) : (
        <CustomTable
          initialDataSource={filteredData}
          columns={columns}
          pagination={{ pageSize: 10 }}
          rowKey={(record: any) => `${record.workPlaceId}-${record.deviceTypeId}`}
          rowClassName={(record) => (record.addition < 50 ? 'warm-red-row' : '')}
          summary={() => (
            <SummaryTotalRow
              getTotalAchizitionPrice={getTotalAchizitionPrice}
              getTotalPriceWithAdition={getTotalPriceWithAdition}
              isMobile={false}
            />
          )}
        />
      )}

      {/* Detailed price summary */}
      {renderDetailedPriceSummary()}
    </div>
  );
};

export default SummaryTab;
